<template>
  <div class="work-check-bpmn">
    <div class="work-check-bpmn__header">
      <h2 class="work-check-bpmn__title">BPMN — Схемы процессов приёмки</h2>
      <CustomButton
        v-if="hasSchemaEditGrant"
        color="primary"
        class="work-check-bpmn__new-schema-btn"
        @click="openDuplicateSchemaModal"
      >
        Новая схема <q-icon name="add" />
      </CustomButton>
    </div>

    <q-linear-progress v-if="isSchemasLoading" indeterminate class="tw-mb-2" />

    <div class="work-check-bpmn__tabs">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        :class="['bpmn-tab', { 'bpmn-tab--active': activeTab === tab.id }]"
        @click="activeTab = tab.id"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Справочники -->
    <template v-if="activeTab === 'dicts'">
      <BpmnDictsEditor />
    </template>

    <!-- Помощь разработчику -->
    <template v-else-if="activeTab === 'help'">
      <BpmnHelp />
    </template>

    <!-- Экземпляры процессов -->
    <template v-else-if="activeTab === 'instances'">
      <WorkCheckBpmnInstances />
    </template>

    <!-- Схемы BPMN -->
    <template v-else-if="activeTab !== 'help' && activeTab !== 'dicts'">
      <div class="work-check-bpmn__schema-info">
        <span class="schema-badge">Схема {{ activeTab }}</span>
        <span class="schema-desc" v-if="!!activeSchemaDescription">{{ activeSchemaDescription }}</span>
      </div>
      <BpmnFlow :schema-id="parseInt(activeTab)" />
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import BpmnFlow from './BpmnFlow.vue'
import BpmnHelp from './BpmnHelp.vue'
import BpmnDictsEditor from './BpmnDictsEditor.vue'
import WorkCheckBpmnInstances from './WorkCheckBpmnInstances.vue'
import { useBpmnDicts } from './useBpmnDicts'
import { getSchemas, getSchemaEditAccess, duplicateBpmnSchema } from './services/services'
import { useAppStore } from '@/services/store/app'
import { MODAL_NAMES } from '@/constants'
import CustomButton from '@/components/common/CustomButton.vue'
import { useCustomSnackbar } from '@/_helpers/hooks/useCustomSnackbar'

const bpmnDicts = useBpmnDicts()
const appStore = useAppStore()
const queryClient = useQueryClient()
const { showSucceedSnackbar } = useCustomSnackbar()

const activeTab = ref('1')

const { data: schemasPayload, isLoading: isSchemasLoading } = useQuery({
  queryKey: ['wcheckBpmnSchemaTab'],
  queryFn: () => getSchemas(),
  refetchOnWindowFocus: false,
})

const { data: hasSchemaEditGrant } = useQuery({
  queryKey: ['bpmnSchemaEditAccess'],
  queryFn: getSchemaEditAccess,
  refetchOnWindowFocus: false,
})

const schemaRows = computed(() => {
  const raw = schemasPayload.value
  return Array.isArray(raw) ? raw : []
})

const schemaTabs = computed(() =>
  schemaRows.value.map((row) => ({
    id: String(row.schema_id),
    label: row.schema_name || `Схема ${row.schema_id}`,
  })),
)

const tabs = computed(() => [
  ...schemaTabs.value,
  { id: 'dicts', label: '📋 Справочники' },
  { id: 'help', label: '📖 Помощь' },
])

watch(
  () => [schemaTabs.value, isSchemasLoading.value],
  ([list, loading]) => {
    if (loading) return
    const meta = new Set(['instances', 'dicts', 'help'])
    if (meta.has(activeTab.value)) return
    if (!list.length) {
      activeTab.value = 'instances'
      return
    }
    const ids = new Set(list.map((t) => t.id))
    if (!ids.has(activeTab.value)) {
      activeTab.value = list[0].id
    }
  },
  { immediate: true },
)

const activeSchemaDescription = computed(() => {
  const row = schemaRows.value.find((s) => String(s.schema_id) === activeTab.value)
  return row?.schema_desc ?? ''
})

const duplicateSchemaSelectOptions = computed(() =>
  schemaRows.value.map((row) => ({
    schema_id: row.schema_id,
    label: row.schema_name || `Схема ${row.schema_id}`,
  })),
)

function openDuplicateSchemaModal() {
  appStore.showModal(MODAL_NAMES.DuplicateBpmnSchema, {
    title: 'Новая схема BPMN',
    schemaOptions: duplicateSchemaSelectOptions.value,
    onSubmit: async (body) => {
      const res = await duplicateBpmnSchema(body)
      if (!res) return
      await queryClient.refetchQueries({ queryKey: ['wcheckBpmnSchemaTab'] })
      appStore.closeModal()
      activeTab.value = String(res.schema_id)
      showSucceedSnackbar()
    },
  })
}

onMounted(() => {
  bpmnDicts.load()
})
</script>

<style scoped lang="scss">
.work-check-bpmn {
  padding: 20px;
  max-width: 1600px;
}

.work-check-bpmn__header {
  margin-bottom: 16px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
}

.work-check-bpmn__new-schema-btn {
  flex-shrink: 0;
  width: fit-content;
  padding: 5px;
  height: 36px;
}

.work-check-bpmn__title {
  font-size: 20px;
  font-weight: 700;
  color: #212121;
  margin: 0;
}

.work-check-bpmn__tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.bpmn-tab {
  padding: 8px 16px;
  border: 2px solid #b0bec5;
  border-radius: 20px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
  color: #546e7a;
  transition: all 0.2s;
}

.bpmn-tab:hover {
  border-color: #1976d2;
  color: #1976d2;
}

.bpmn-tab--active {
  border-color: #1976d2;
  background: #1976d2;
  color: #fff;

  &:hover {
    color: #fff;
  }
}

.work-check-bpmn__schema-info {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.schema-badge {
  padding: 4px 10px;
  border-radius: 12px;
  background: #e3f2fd;
  color: #1565c0;
  font-size: 12px;
  font-weight: 600;
}

.schema-desc {
  font-size: 13px;
  color: #546e7a;
}

</style>
